// @flow
import type {StoreFeature} from '../entities/store-feature';

export type FeatureInfo = {
  name: string,
  description: string,
  notes?: string,
};

export type FeatureGroup = {
  id: string,
  name: string,
  features: StoreFeature[],
};

export const STORE_FEATURES_INFO: {[StoreFeature]: FeatureInfo} = Object.freeze(
  {
    // === ORDERS ===
    ORDER_MANAGEMENT_DASHBOARD: {
      name: 'Dashboard de Pedidos',
      description: 'Panel central de administración de pedidos',
    },
    ORDER_STATUS_CHANGE: {
      name: 'Cambio de estado de pedidos',
      description: 'Actualizar los estados de los pedidos',
    },
    MANUAL_ORDER_CREATION: {
      name: 'Creación manual de pedidos',
      description: 'Generar pedidos directamente desde el panel',
    },
    ORDER_REVIEW: {
      name: 'Reseñas de compra',
      description: 'Gestión de comentarios y valoraciones',
    },
    ORDER_FILTER: {
      name: 'Filtros de pedidos',
      description: 'Filtrar pedidos por estado o fecha',
    },
    ORDER_EDIT_COLLABORATOR: {
      name: 'Asignar colaborador a pedido',
      description: 'Vincular personal a pedidos específicos',
    },
    ORDER_EDIT_CUSTOMER_DATA: {
      name: 'Editar datos del cliente',
      description: 'Modificar información de contacto y envío',
    },
    ORDER_EDIT_ITEMS: {
      name: 'Editar productos',
      description: 'Agregar o quitar productos de una orden existente',
    },
    ORDER_EDIT_DELIVERY_DATA: {
      name: 'Editar datos de entrega',
      description: 'Cambiar método o dirección de despacho de un pedido',
    },
    ORDER_EDIT_ADDITIONAL_INFORMATION: {
      name: 'Editar info adicional de pedido',
      description: 'Modificar notas o campos personalizados',
    },
    ORDER_EXPORT: {
      name: 'Exportar pedidos',
      description: 'Descarga masiva de órdenes en Excel/CSV',
    },
    ORDER_NOTIFICATIONS: {
      name: 'Notificaciones de pedidos',
      description: 'Alertas automáticas por correo o panel',
    },
    ORDER_COMMENTS: {
      name: 'Comentarios internos de pedido',
      description: 'Notas privadas para el equipo',
    },
    ORDER_REQUIRED_INFORMATION: {
      name: 'Información adicional del pedido',
      description: 'Configurar campos requeridos en el checkout',
    },
    ORDER_STATE_CHANGE_MESSAGES: {
      name: 'Mensajes en los cambios de estado',
      description:
        'Configurar y usar un mensaje personalizado para los cambios de estado',
    },
    ORDER_EDIT_PAYMENT_DATA: {
      name: 'Editar datos de pago del pedido',
      description: 'Actualizar método de pago del pedido',
    },
    PRE_ORDER: {
      name: 'Crear pre pedidos',
      description: 'Permitir la creación de pre pedidos',
    },
    CUSTOM_ORDER_CHECKOUT: {
      name: 'Campos personalizados en la creación de pedidos',
      description: 'Agregar campos personalizados en la creación de pedidos',
    },
    ORDER_LIMIT: {
      name: 'Límite de pedidos',
      description: 'Restricción máxima de órdenes recibidas',
      notes: 'INTERNAL',
    },

    // === PRODUCTOS ===
    PRODUCT_LIMIT: {
      name: 'Límite de productos',
      description: 'Capacidad máxima de catálogo',
      notes: 'INTERNAL',
    },
    VARIOUS_PRODUCT_IMAGES: {
      name: 'Múltiples imágenes',
      description: 'Subir más de una foto por producto',
      notes: 'INTERNAL',
    },
    STOCK_BY_PRODUCT: {
      name: 'Administrar el stock de productos',
      description: 'Gestionar el stock de los productos',
    },
    MASSIVE_PRODUCT_UPLOAD: {
      name: 'Carga masiva',
      description: 'Crear y editar productos en base a un archivo Excel',
    },
    PRODUCT_VIDEO_URL: {
      name: 'Video de producto',
      description: 'Añadir links de YouTube, TikTok, etc.',
    },
    BULK_PRICE: {
      name: 'Precio por mayor',
      description: 'Configurar precios por mayor por producto',
    },
    FEATURED_PRODUCTS: {
      name: 'Productos destacados',
      description: 'Configurar productos destacados',
    },
    PRODUCT_VARIANTS: {
      name: 'Variantes de producto',
      description: 'Configurar variantes de producto',
    },
    PRODUCT_CATALOGUE: {
      name: 'Catálogo de productos',
      description: 'Mostrar catalogo PDF de la tienda',
    },
    PRODUCT_CREATION: {
      name: 'Crear productos',
      description: 'Agregar nuevos productos al inventario',
    },
    PRODUCT_EDIT: {
      name: 'Editar productos',
      description: 'Modificar información de productos existentes',
    },
    PRODUCT_DELETE: {
      name: 'Eliminar productos',
      description: 'Quitar productos permanentemente',
    },
    PRODUCT_DISABLE: {
      name: 'Desactivar productos',
      description: 'Ocultar productos sin borrar',
    },
    INSTAGRAM_PRODUCT_UPLOAD: {
      name: 'Subida desde Instagram',
      description: 'Cargar productos desde Instagram',
    },
    PRODUCT_SEO: {
      name: 'SEO de producto',
      description: 'Configurar metadatos para buscadores',
    },
    PRODUCT_SPECIFICATIONS: {
      name: 'Características del producto',
      description: 'Configurar las características del producto',
    },
    RELATED_PRODUCTS: {
      name: 'Productos relacionados',
      description:
        'Configurar sugerencias de productos en el detalle de cada producto',
    },
    PRODUCT_SALES: {
      name: 'Mostrar x personas que compraron este producto',
      description:
        'Activar o desactivar la funcionalidad de mostrar la cantidad de personas que compraron este producto',
    },
    PRODUCT_CTA: {
      name: 'Botones de acción',
      description:
        'Configurar botones de acción (Carrito, Comprar ahora, Pedir por whatsapp)',
    },
    PRODUCT_SIZE_CHART: {
      name: 'Guía de tallas',
      description: 'Configurar la guía de tallas',
    },
    PRODUCT_LIVE_VIEWS: {
      name: 'Mostrar cantidad de personas mirando tu producto',
      description:
        'Activar o desactivar la funcionalidad de mostrar la cantidad de personas que están viendo tu producto',
    },
    PRODUCT_FILTER: {
      name: 'Filtros de búsqueda',
      description: 'Buscador de productos por combinación de variantes',
    },
    PRODUCT_SECTIONS: {
      name: 'Secciones de producto',
      description: 'Organizar por bloques de información',
    },
    PRODUCTS_BULK_EDIT: {
      name: 'Editor de productos',
      description: 'Actualizar múltiples productos a la vez',
    },
    SORT_PRODUCT_IMAGES: {
      name: 'Ordenar imágenes del producto',
      description: 'Actualizar el orden de las fotos del producto',
    },
    PRODUCT_OPTIONS: {
      name: 'Opciones extras',
      description: 'Configurar y crear opciones de productos',
    },

    // === CATEGORÍAS ===
    CATEGORY_LIMIT: {
      name: 'Límite de categorías',
      description: 'Cantidad máxima de categorías',
      notes: 'INTERNAL',
    },
    CATEGORY_PHOTOS: {
      name: 'Fotos de categoría',
      description: 'Permite subir imágenes a las categorías',
    },
    HOME_CATEGORIES_CAROUSEL: {
      name: 'Nuevo diseño de categorías',
      description: 'Actualizacion de la nueva vista de categorias',
      notes:
        'Feature que no aporta mucho valor, ya que la version antigua de categorias ya no esta disponible',
    },
    PRODUCT_CATEGORIES: {
      name: 'Asignar categorías',
      description:
        'Crear, editar, eliminar y gestionar asignar categorías a los productos',
    },
    PRODUCT_CATEGORY_LAYOUT: {
      name: 'Diseño de lista de categorías',
      description: 'Cambiar el diseño de la lista de categorías',
    },

    // === STORE CONFIG ===
    COLOR_CONFIGURATION: {
      name: 'Colores básicos',
      description: 'Cambiar los colores predeterminados de la tienda',
    },
    ADVANCED_COLOR_CONFIGURATION: {
      name: 'Colores avanzados',
      description: 'Configurar los colores personalizados de la tienda',
    },
    FONT_CONFIGURATION: {
      name: 'Tipografías',
      description: 'Cambiar las fuentes predeterminadas de la tienda',
    },
    IMAGE_CONFIGURATION: {
      name: 'Forma de las imágenes de productos',
      description:
        'Cambiar la forma de las imágenes de los productos (cuadrada, rectangular)',
    },
    STOREFRONT_TEMPLATES: {
      name: 'Plantillas',
      description: 'Cambiar entre las plantillas predeterminadas de la tienda',
    },
    CUSTOM_DOMAIN: {
      name: 'Dominio propio',
      description: 'Configurar un dominio propio para la tienda',
    },
    STORE_INFORMATION: {
      name: 'Información de la tienda',
      description:
        'Datos de contacto, ubicación, tipo de tienda, pais, telefono, correo, etc',
    },
    STORE_COLLABORATOR: {
      name: 'Colaboradores',
      description: 'Gestión de accesos y permisos de los colaboradores',
    },
    STORE_COLLABORATOR_LIMIT: {
      name: 'Límite de personal',
      description: 'Máximo de usuarios',
      notes: 'INTERNAL',
    },
    STORE_POLICIES: {
      name: 'Políticas',
      description:
        'Configurar las políticas de la tienda (terminos, privacidad, devoluciones, etc)',
    },
    STORE_SOCIAL_MEDIA: {
      name: 'Redes sociales',
      description: 'Configurar los links a perfiles externos',
    },
    STORE_LOCATION: {
      name: 'Ubicación',
      description: 'Configurar las direcciones de la tienda',
    },
    STORE_FAVICON: {
      name: 'Favicon',
      description: 'Configurar el icono de la tienda',
    },

    // === DASHBOARD ===
    DASHBOARD_PRO: {
      name: 'Dashboard Pro',
      description: 'Métricas avanzadas y reportes',
    },
    DASHBOARD_BASIC: {
      name: 'Dashboard Básico',
      description: 'Vista general simple',
    },
    DASHBOARD_COLLABORATOR: {
      name: 'Dashboard Colaborador',
      description: 'Vista limitada para personal',
    },

    // === SHIPPING & PAYMENT ===
    ADVANCED_SHIPPING_METHOD: {
      name: 'Envíos avanzados',
      description: 'Zonas y reglas complejas',
      notes: 'en uso desde el backend pero no se maneja en el frontend',
    },
    DELIVERY_OPTIONS: {
      name: 'Opciones de entrega',
      description:
        'Configurar los tipos de envíos, recogida y promociones de envío',
    },
    MY_PAYMENT_INFORMATION: {
      name: 'Configurar nuevos metodos de pago',
      description:
        'Configurar nuevos metodos de pago que no estan disponibles por defecto',
    },
    PAYMENT_PROCESSOR: {
      name: 'Metodos de pago',
      description: 'Configurar los metodos de pago',
    },
    MERCADOPAGO_CHECKOUT_PRO_INTEGRATION: {
      name: 'Mercado Pago Pro',
      description: 'Configurar la integracion de mercado pago',
    },

    // === PROMOCIONES ===
    STORE_COUPON: {
      name: 'Cupones',
      description: 'Configurar cupones',
    },
    STORE_PROMOTION: {
      name: 'Promociones',
      description: 'Configurar promociones',
    },
    MARKETING_TEMPLATES: {
      name: 'Plantillas marketing',
      description: 'Diseños para campañas',
      notes:
        'En uso pero, la vista que pertenece a esta feature no es visible desde el frontend',
    },

    // === CRM ===
    CRM_CUSTOMER: {
      name: 'Base de clientes',
      description: 'Administrar clientes',
    },
    CRM_SEGMENT: {
      name: 'Segmentos',
      description: 'Configurar los segmentos',
    },
    CUSTOMER_COMPLAINT: {
      name: 'Libro de reclamaciones',
      description: 'Gestionar quejas',
    },

    // === FACTURACIÓN ===
    TAXES: {
      name: 'Impuestos',
      description: 'Configurar Impuestos',
    },
    TAX_INVOICE: {
      name: 'Facturación electrónica',
      description: 'Configurar Facturación electrónica',
    },
    STORE_TAX_INVOICE_SETTING: {
      name: 'Configuración fiscal',
      description: 'Configurar datos de facturación',
    },

    // === INTEGRACIONES ===
    FB_STORE: {
      name: 'Tienda FB',
      description: 'Sincronizar con Facebook',
    },
    FACEBOOK_PIXEL_ID: {
      name: 'Píxel de Facebook',
      description: 'Configurar píxel de Facebook',
    },
    FACEBOOK_VALIDATION_TOKEN: {
      name: 'Token de validación FB',
      description: 'Configurar token de validación de Facebook',
    },
    GOOGLE_ANALYTICS_TRACKING: {
      name: 'Google Analytics',
      description: 'Configurar integracion de Google Analytics',
    },
    GOOGLE_TAG_MANAGER: {
      name: 'GTM',
      description: 'Configurar integracion de Google Tag Manager',
    },
    TIKTOK_PIXEL_ID: {
      name: 'Píxel de TikTok',
      description: 'Configurar píxel de TikTok',
    },

    // === MULTICHAT ===
    WHATSAPP_NOTIFICATIONS: {
      name: 'Avisos WhatsApp',
      description: 'Alertas por chat',
      notes: 'No hay una vista que use esta feature',
    },
    TIENDACHAT_WHATSAPP_WEB: {
      name: 'WhatsApp Web',
      description: 'Configurar tiendachat con whatsapp web',
    },
    TIENDACHAT_QUICK_REPLIES: {
      name: 'Respuestas rápidas',
      description: 'Configurar las respuestas rápidas',
    },
    TIENDACHAT_TRIGGERS: {
      name: 'Disparadores',
      description: 'Configurar los disparadores automaticos',
    },
    FUNNEL: {
      name: 'Embudo',
      description: 'Configurar el embudo',
    },
    TIENDACHAT_FB_MESSENGER: {
      name: 'FB Messenger',
      description: 'Configurar tiendachat con facebook messenger',
    },
    TIENDACHAT_INSTAGRAM: {
      name: 'Instagram Direct',
      description: 'Configurar tiendachat con Instagram Direct',
    },
    TIENDACHAT_WHATSAPP_API: {
      name: 'WhatsApp API',
      description: 'Configurar tiendachat con whatsapp api',
    },
    TIENDACHAT_AI_CHATBOT: {
      name: 'IA Chatbot',
      description: 'Configurar tiendachat con chatbot',
    },

    // === CONTENIDO ===
    COVER_PHOTO: {
      name: 'Foto de portada',
      description: 'Configurar la foto de portada',
    },
    STORE_PROFILE_INFORMATION: {
      name: 'Perfil de tienda',
      description: 'Configurar el perfil de la tienda',
    },
    BANNER: {
      name: 'Banners ',
      description: 'Configurar los banners de la tienda',
    },
    STORE_POP_UP: {
      name: 'Pop-up',
      description: 'Configurar el pop-up de la tienda',
    },
    STORE_BLOG: {
      name: 'Blog',
      description: 'Configurar el blog de la tienda',
    },
    STORE_HEADER: {
      name: 'Encabezado',
      description: 'Configurar el banner del encabezado de la tienda',
    },
    BANNER_GROUP: {
      name: 'Grupo de banners',
      description: 'Configurar el banner del grupo de banners',
    },
    STORE_PAGE_LAYOUT: {
      name: 'Diseño de página',
      description: 'Configurar el orden de los bloques de la página principal',
    },
    STORE_INFORMATION_BLOCK: {
      name: 'Bloque info',
      description: 'Configurar el bloque de información de la tienda',
    },
  }
);
